// @flow
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  InsuranceVerificationDetails,
  Typography,
} from 'src/latest/src/components';
import withLoading from '../../components/modules/withLoading/withLoading';
import {
  handleQuestionRender,
  populateQuestionnaireImage,
  populateQuestionnaireTooltip,
  sanitiseHTML,
} from '../../legacy/utils/helpers';
import QuestionContainer from './containers/QuestionContainer/QuestionContainer';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import './questionnairePage.scss';
import VerticalProfile from '../../components/base/VerticalProfile/VerticalProfile';
import { useIsMobile } from 'src/latest/src/hooks';
import {
  clearModalContent,
  setModalContent,
} from 'src/redux/modules/modalEvent';
import { noop } from 'react-bootstrap-typeahead/types/utils';
import WhyInfoModal from './components/WhyInfoModal/whyInfoModal';
import { PageUniqueKeys, QuestionsUniqueKeys } from './QuestionnaireEnum';

type Props = {
  currentPage: object,
  fromEditProfile: Boolean,
  setUploading: any,
  setIsFileTypeInvalid: any,
  isFileTypeInvalid: Boolean,
  handleOnclick: Function,
  setIsMatchAssistance?: (isMatchAssistance: boolean) => void,
  onStripeCardStatusChange?: (hasCard: boolean) => void,
  onStripeCardFormValidationChange?: (isValid: boolean) => void,
  onStripeProcessingChange?: (isProcessing: boolean) => void,
  stripePaymentRef?: any,
  isStripeProcessing?: Boolean,
};

function QuestionnairePage({
  currentPage,
  fromEditProfile,
  setUploading,
  isFileTypeInvalid,
  setIsFileTypeInvalid,
  handleOnclick,
  setIsMatchAssistance = noop,
  onStripeCardStatusChange,
  onStripeCardFormValidationChange,
  onStripeProcessingChange,
  stripePaymentRef,
  isStripeProcessing,
}: Props): React$Element<any> {
  const [hideTitle, setHideTitle] = useState(false);
  const dispatch = useDispatch();
  const questionnairePage = useRef(null);
  const pageName = currentPage?.name;
  const currentQuestions = useSelector(
    state => state?.questionnaireData?.currentConfig?.currentQuestions,
  );
  const questionnaireType = useSelector(
    state =>
      state?.questionnaireData?.initialMetadata?.questionnaire_group?.type,
  );
  const userId = useSelector(
    state => state?.questionnaireData?.initialMetadata?.user_id,
  );
  const isFromMobile = useIsMobile();

  useEffect(() => {
    return () => {
      document.removeEventListener('click', noop);
    };
  }, []);

  const addEventListeners = () => {
    document.addEventListener(
      'click',
      e =>
        e.target.classList.contains('btn-why') &&
        dispatch(
          setModalContent({
            modalContent: <WhyInfoModal />,
            title: 'Why do we share this info with your therapist?',
            className: 'client_checkIn_modal',
            showCloseButton: true,
            isDisableClose: false,
            toggle: () => clearModalContent(),
          }),
        ),
    );
  };

  useEffect(() => {
    const btnWhy = document.querySelector('.btn-why');

    if (
      btnWhy &&
      currentPage.unique_key === PageUniqueKeys?.clientCheckinPageV1
    ) {
      btnWhy.addEventListener('click', addEventListeners);
    }

    if (!fromEditProfile && currentQuestions) {
      for (const question of currentQuestions) {
        if (question?.metadata?.hide_question_title === 'true') {
          setHideTitle(true);

          return;
        }
      }
    }

    setHideTitle(false);
  }, [currentQuestions]);

  // rerun on page updated otherwise we need to wait for the html to load using the set time out
  useEffect(() => {
    populateQuestionnaireImage(questionnairePage);
    populateQuestionnaireTooltip(questionnairePage);
  });

  function convertUTCDateToLocalDate(date) {
    const newDate = new Date(
      date.getTime() + date.getTimezoneOffset() * 60 * 1000,
    );

    const offset = date.getTimezoneOffset() / 60;
    const hours = date.getHours();

    newDate.setHours(hours - offset);

    return newDate;
  }

  const enhancedInformation = useSelector(
    state => state?.questionnaireData?.initialMetadata?.enhanced_information,
  );
  const localDate = convertUTCDateToLocalDate(
    new Date(enhancedInformation?.therapySessionTime),
  );

  // const dispatch = useDispatch();
  // const isGridLayoutPresent = currentQuestions.filter((question) =>  question?.metadata?.question_layout === "grid").length > 0;

  // 32

  const isSlider = question => {
    if (question?.title && question?.title !== '' && question?.title !== null) {
      return question?.metadata?.question_type === 'customslider'
        ? 'slider-opt'
        : '';
    } else if (
      !question?.title ||
      question?.title === '' ||
      question?.title === null
    ) {
      return question?.metadata?.question_type === 'customslider'
        ? 'slider-opt-no-title'
        : '';
    }

    return '';
  };

  return (
    <div
      ref={questionnairePage}
      className={`questionnaire-page sv-root-modern ${
        isFromMobile ? '' : 'mt-5'
      } ${currentPage?.unique_key} ${
        questionnaireType && questionnaireType !== 'SELF_MATCHING_CLIENT_INTAKE'
          ? 'padding-top'
          : ''
      }`}
    >
      {enhancedInformation && (
        <div className="therapist-checkout-details-container mb-5">
          <div className="header-text-container">
            <Typography className="pending-checkout-page-sub-header mb-5">
              You didn't complete a checkout for this session
            </Typography>
          </div>
          <div className="therapist-card-container">
            <VerticalProfile
              className="vertical-profile-checkout mt-4 mb-4"
              fullName={enhancedInformation.fullName}
              image={enhancedInformation.profile_pic}
              userId={enhancedInformation.therapistUserId}
              children={
                <>
                  <Typography className="vertical-profile-checkout-subtitle size-16 color-28 mb-2">
                    {enhancedInformation.therapyType}
                  </Typography>
                  <Typography
                    weight="bold"
                    className="weight-bold size-26 vertical-profile-checkout-session-info"
                    size="size-24"
                  >
                    {' '}
                    <i>
                      on {localDate.toLocaleDateString()} at{' '}
                      {localDate.toLocaleTimeString()}
                    </i>
                  </Typography>
                </>
              }
              isRequiredCredentials={true}
              credentials={enhancedInformation.credentials}
            />
          </div>
        </div>
      )}

      {(currentPage?.header ||
        currentPage?.page_meta_description ||
        currentPage?.sub_header ||
        currentPage?.description) && (
        <div className="titles-container">
          {currentPage?.header && (
            <Typography
              className={`text-center ${
                pageName?.replace(/ /g, '-') === 'Therapist-Checkout-Page'
                  ? 'mb-2'
                  : 'mb-4'
              } ${pageName?.replace(/ /g, '-')} `}
            >
              {sanitiseHTML(currentPage?.header)}
            </Typography>
          )}
          {currentPage?.page_meta_description && (
            <Typography
              className={`text-center mb-4 ${pageName?.replace(/ /g, '-')} `}
            >
              {sanitiseHTML(currentPage?.page_meta_description)}
            </Typography>
          )}
          {currentPage?.sub_header && (
            <Typography
              className={`text-center mb-4 ${pageName?.replace(/ /g, '-')} `}
            >
              {sanitiseHTML(currentPage?.sub_header)}
            </Typography>
          )}
          {currentPage?.description && (
            <Typography
              className={`text-center mb-4 ${pageName?.replace(/ /g, '-')}`}
            >
              {sanitiseHTML(currentPage?.description, isFromMobile)}
            </Typography>
          )}
        </div>
      )}

      <Box sx={{ width: '100%', margin: 'auto', display: 'flex' }}>
        <Grid
          container={true}
          rowSpacing={1}
          spacing={4}
          sx={{ maxWidth: '830px', margin: 'auto' }}
        >
          {currentQuestions &&
            currentQuestions.map(question => {
              // conditional question rendering - question level , not choice level
              const renderCurrent = handleQuestionRender(
                question,
                currentQuestions,
              );

              return (
                renderCurrent && (
                  <Grid
                    item={true}
                    sm={question?.metadata?.question_layout === 'grid' ? 6 : 12}
                    justifyContent={
                      question?.metadata?.question_layout === 'grid'
                        ? 'flex-start'
                        : 'center'
                    }
                    className={`${question.unique_key} ${
                      question?.metadata?.question_layout === 'grid'
                        ? `grid-opt`
                        : !question?.title &&
                          question?.metadata?.hide_question_title === 'true'
                        ? `padding-none`
                        : `no-grid ${isSlider(question)}`
                    }`}
                    sx={
                      question?.metadata?.question_layout === 'grid'
                        ? {
                            minHeight: hideTitle ? 'auto' : '93px',
                            height: 'auto !important',
                          }
                        : {}
                    }
                    key={question.id}
                  >
                    {question.unique_key ===
                    QuestionsUniqueKeys?.benefitsSummaryInsuranceDetailsQuestion ? (
                      <InsuranceVerificationDetails
                        setUploading={setUploading}
                        question={question}
                        userId={userId}
                      />
                    ) : (
                      <QuestionContainer
                        className={question.id}
                        key={question.id}
                        questionTitle={question.title}
                        questionDescription={question.description}
                        question={question}
                        setUploading={setUploading}
                        isFileTypeInvalid={isFileTypeInvalid}
                        setIsFileTypeInvalid={setIsFileTypeInvalid}
                        handleOnclick={handleOnclick}
                        setIsMatchAssistance={setIsMatchAssistance}
                        onStripeCardStatusChange={onStripeCardStatusChange}
                        onStripeCardFormValidationChange={
                          onStripeCardFormValidationChange
                        }
                        onStripeProcessingChange={onStripeProcessingChange}
                        stripePaymentRef={stripePaymentRef}
                        isStripeProcessing={isStripeProcessing}
                      />
                    )}
                  </Grid>
                )
              );
            })}
        </Grid>
      </Box>
    </div>
  );
}

const Questionnaire: any = withLoading<React$Element<Props>>(QuestionnairePage);

export default Questionnaire;
